/**
 * ProductSummaryChipsPage — Products & Features · Product Summary Chips.
 *
 * Per-(product × chip) summary chip set rendered on each populated-cart
 * product card (Screen 25 SPEC). Migrated from the legacy ROP-teams editor
 * (apps/ROP-teams/src/modules/Admin/editors/productSummaryChipConfigEditor.tsx)
 * to the admin-panel SWA, matching the ProductsPage CRUD reference: reload via
 * config read, DataTable with trailing Edit/Delete ghost buttons, a top "New"
 * Card action, a ModalShell form built from FormField primitives, a save()
 * handler, and a delete confirm.
 *
 * Reads /v3/config/product-summary-chip-config (rows) + /v3/config/products
 * (FK source for the productKey dropdown). Writes go through the typed,
 * bearer-only admin surface (first arg '' = no bypass key):
 *   client.retail.admin.products.productSummaryChips.upsert('', body)
 *   client.retail.admin.products.productSummaryChips.remove('', productKey, chipKey)
 *
 * chipKey is FREE-TEXT (a monospace TextField), exactly as in the legacy
 * editor where it was a plain <input> ("Chip key (e.g. Deposit, Services,
 * CdTerm)"). The legacy editor did NOT constrain it to an enum, so we do not
 * invent one here.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Product } from '@retail-ops/shared-types';
import type { ProductSummaryChipConfigUpsertBody } from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, SelectField, BoolField, NumberField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

/**
 * Wire row shape returned by GET /v3/config/product-summary-chip-config. The
 * config read is typed as `ReadonlyArray<Record<string, unknown>>`, so we cast
 * each row to this local interface (mirrors the legacy editor's `Row`).
 */
interface Row {
  productKey: string;
  chipKey: string;
  displayOrder: number | null;
  visible: boolean;
  labelOverride: string | null;
}

interface EditDraft {
  isNew: boolean;
  productKey: string;
  chipKey: string;
  displayOrder: number | null;
  visible: boolean;
  labelOverride: string;
}

function toNum(v: unknown): number | null {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function toStr(v: unknown): string {
  return v == null ? '' : String(v);
}

function castRow(r: Record<string, unknown>): Row {
  return {
    productKey: toStr(r.productKey),
    chipKey: toStr(r.chipKey),
    displayOrder: toNum(r.displayOrder),
    visible: r.visible !== false,
    labelOverride: r.labelOverride == null || r.labelOverride === '' ? null : String(r.labelOverride),
  };
}

function rowToDraft(r: Row): EditDraft {
  return {
    isNew: false,
    productKey: r.productKey,
    chipKey: r.chipKey,
    displayOrder: r.displayOrder,
    visible: r.visible,
    labelOverride: r.labelOverride ?? '',
  };
}

function blankDraft(): EditDraft {
  return {
    isNew: true,
    productKey: '',
    chipKey: '',
    displayOrder: 100,
    visible: true,
    labelOverride: '',
  };
}

function draftToBody(d: EditDraft): ProductSummaryChipConfigUpsertBody {
  const labelOverride = d.labelOverride.trim();
  return {
    productKey: d.productKey.trim(),
    chipKey: d.chipKey.trim(),
    displayOrder: d.displayOrder,
    visible: d.visible,
    labelOverride: labelOverride === '' ? null : labelOverride,
  };
}

export function ProductSummaryChipsPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<Row> | null>(null);
  const [products, setProducts] = useState<ReadonlyArray<Product>>([]);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('');
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const [chipResp, prodResp] = await Promise.all([
        client.retail.config.productSummaryChipConfig(),
        client.retail.config.products(),
      ]);
      if (chipResp.success && Array.isArray(chipResp.data)) {
        setRows(chipResp.data.map(castRow));
      } else {
        setError(chipResp.error ?? 'Failed to load product summary chips.');
        return;
      }
      if (prodResp.success && Array.isArray(prodResp.data)) {
        setProducts(prodResp.data);
      } else {
        setProducts([]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [client]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // FK dropdown options for productKey — built from the live products catalog.
  const productOptions = useMemo(
    () => [
      { value: '', label: '— pick a product —' },
      ...products.map((p) => ({ value: p.code, label: `${p.code} — ${p.name}` })),
    ],
    [products],
  );

  const productName = useCallback(
    (code: string): string | null => products.find((p) => p.code === code)?.name ?? null,
    [products],
  );

  const filtered = useMemo(() => {
    if (!rows) return rows;
    if (!filter.trim()) return rows;
    const n = filter.toLowerCase();
    return rows.filter(
      (r) =>
        r.productKey.toLowerCase().includes(n) ||
        r.chipKey.toLowerCase().includes(n) ||
        (r.labelOverride ?? '').toLowerCase().includes(n) ||
        (productName(r.productKey) ?? '').toLowerCase().includes(n),
    );
  }, [rows, filter, productName]);

  const remove = useCallback(
    async (productKey: string, chipKey: string): Promise<void> => {
      if (!window.confirm(`Delete summary chip ${productKey} / ${chipKey}?`)) {
        return;
      }
      setError(null);
      try {
        const resp = await client.retail.admin.products.productSummaryChips.remove('', productKey, chipKey);
        if (resp.success === false) {
          setError(resp.error ?? 'Delete failed.');
          return;
        }
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [client, reload],
  );

  const columns: ReadonlyArray<DataTableColumn<Row>> = useMemo(
    () => [
      {
        header: 'Product',
        cell: (r) => (
          <span>
            <code className="cell-mono--bold">{r.productKey}</code>
            {productName(r.productKey) && (
              <span className="cell-dim"> · {productName(r.productKey)}</span>
            )}
          </span>
        ),
        sortKey: (r) => r.productKey,
      },
      {
        header: 'Chip',
        cell: (r) => <code className="cell-mono">{r.chipKey}</code>,
        sortKey: (r) => r.chipKey,
      },
      {
        header: 'Order',
        cell: (r) =>
          r.displayOrder != null ? (
            <span className="cell-mono">{r.displayOrder}</span>
          ) : (
            <span className="cell-dim">—</span>
          ),
        sortKey: (r) => r.displayOrder,
      },
      {
        header: 'Visible',
        cell: (r) => (r.visible ? <Pill tone="ok">yes</Pill> : <Pill tone="muted">no</Pill>),
        sortKey: (r) => (r.visible ? 1 : 0),
      },
      {
        header: 'Label Override',
        cell: (r) => r.labelOverride ?? <span className="cell-dim">—</span>,
      },
      {
        header: '',
        cell: (r) => (
          <div className="cell-actions">
            <Button size="sm" variant="ghost" onClick={() => setDraft(rowToDraft(r))}>
              Edit
            </Button>
            <Button size="sm" variant="ghost" onClick={() => void remove(r.productKey, r.chipKey)}>
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [productName, remove],
  );

  const save = useCallback(
    async (d: EditDraft): Promise<void> => {
      if (!d.productKey.trim() || !d.chipKey.trim()) {
        setSaveErr('Product and chip key are both required.');
        return;
      }
      setSaving(true);
      setSaveErr(null);
      try {
        const body = draftToBody(d);
        const resp = await client.retail.admin.products.productSummaryChips.upsert('', body);
        if (resp.success === false) {
          setSaveErr(resp.error ?? 'Save failed.');
          return;
        }
        setDraft(null);
        await reload();
      } catch (e) {
        setSaveErr(e instanceof Error ? e.message : String(e));
      } finally {
        setSaving(false);
      }
    },
    [client, reload],
  );

  if (error) return <Banner tone="err">Could not load product summary chips: {error}</Banner>;
  if (!rows) return <Spinner label="Loading product summary chips…" />;

  return (
    <>
      <Card
        title={`Product Summary Chips — ${filtered?.length ?? 0} of ${rows.length}`}
        actions={
          <div className="users-filter">
            <TextField
              label=""
              value={filter}
              onChange={setFilter}
              placeholder="Filter by product, chip, or label…"
            />
            <Button onClick={() => setDraft(blankDraft())}>New</Button>
          </div>
        }
      >
        <DataTable<Row>
          columns={columns}
          rows={filtered ?? []}
          rowKey={(r) => `${r.productKey}::${r.chipKey}`}
          emptyState={<p className="empty">No summary chips match the current filter.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={
          draft
            ? draft.isNew
              ? 'New summary chip'
              : `Edit summary chip — ${draft.productKey} / ${draft.chipKey}`
            : ''
        }
        onClose={() => (saving ? undefined : setDraft(null))}
        width={560}
        actions={
          draft ? (
            <>
              <Button variant="ghost" onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={() => void save(draft)} disabled={saving}>
                {saving ? 'Saving…' : draft.isNew ? 'Create' : 'Save'}
              </Button>
            </>
          ) : null
        }
      >
        {draft && (
          <>
            {saveErr && <Banner tone="err">{saveErr}</Banner>}
            <SelectField
              label="Product"
              value={draft.productKey}
              onChange={(v) => setDraft({ ...draft, productKey: v })}
              options={productOptions}
              disabled={!draft.isNew}
              required
              hint={
                draft.isNew
                  ? 'FK to the Products catalog. Part of the primary key.'
                  : 'Read-only after create (primary key).'
              }
            />
            <TextField
              label="Chip Key"
              value={draft.chipKey}
              onChange={(v) => setDraft({ ...draft, chipKey: v })}
              disabled={!draft.isNew}
              monospace
              required
              placeholder="Deposit"
              hint={
                draft.isNew
                  ? 'Free-text chip identifier (e.g. Deposit, Services, CdTerm). Part of the primary key.'
                  : 'Read-only after create (primary key).'
              }
            />
            <NumberField
              label="Display Order"
              value={draft.displayOrder}
              onChange={(v) => setDraft({ ...draft, displayOrder: v })}
              min={0}
              hint="Lower numbers render first."
            />
            <TextField
              label="Label Override"
              value={draft.labelOverride}
              onChange={(v) => setDraft({ ...draft, labelOverride: v })}
              placeholder="Card design"
              hint="Optional. Overrides the default chip label. Leave blank to use the default."
            />
            <BoolField
              label="Visible"
              value={draft.visible}
              onChange={(v) => setDraft({ ...draft, visible: v })}
            />
          </>
        )}
      </ModalShell>
    </>
  );
}
